import React, { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '../ui/button';

const SidebarFilter = ({ block, onFilterChange }) => {
  const { title, filters = [] } = block.content;
  const [activeFilters, setActiveFilters] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleFilterChange = (filterId, value) => {
    const newFilters = {
      ...activeFilters,
      [filterId]: value
    };
    setActiveFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const clearFilters = () => {
    setActiveFilters({});
    if (onFilterChange) {
      onFilterChange({});
    }
  };

  const FilterContent = () => (
    <div className="space-y-6">
      {/* Titre */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Filter className="h-5 w-5" />
          {title || 'Filtres'}
        </h2>
        {Object.keys(activeFilters).length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            className="text-xs"
          >
            Effacer
          </Button>
        )}
      </div>

      {/* Filtres */}
      {filters.map((filter) => (
        <div key={filter.id} className="space-y-2">
          <label className="text-sm font-medium">
            {filter.label}
          </label>

          {/* Type: Select */}
          {filter.type === 'select' && (
            <select
              value={activeFilters[filter.id] || ''}
              onChange={(e) => handleFilterChange(filter.id, e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            >
              <option value="">Tous</option>
              {filter.options?.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          )}

          {/* Type: Range */}
          {filter.type === 'range' && (
            <div className="space-y-2">
              <input
                type="range"
                min={filter.min || 0}
                max={filter.max || 100}
                value={activeFilters[filter.id] || filter.min || 0}
                onChange={(e) => handleFilterChange(filter.id, e.target.value)}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{filter.min || 0}</span>
                <span className="font-medium">{activeFilters[filter.id] || filter.min || 0}</span>
                <span>{filter.max || 100}</span>
              </div>
            </div>
          )}

          {/* Type: Checkbox (multi-select) */}
          {filter.type === 'checkbox' && (
            <div className="space-y-2">
              {filter.options?.map((option) => (
                <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={activeFilters[filter.id]?.includes(option.value) || false}
                    onChange={(e) => {
                      const currentValues = activeFilters[filter.id] || [];
                      const newValues = e.target.checked
                        ? [...currentValues, option.value]
                        : currentValues.filter(v => v !== option.value);
                      handleFilterChange(filter.id, newValues);
                    }}
                    className="rounded border-gray-300"
                  />
                  <span className="text-sm">{option.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:block sticky top-20 h-fit">
        <div className="bg-background border rounded-lg p-6">
          <FilterContent />
        </div>
      </aside>

      {/* Mobile: Bouton Filtres */}
      <div className="lg:hidden">
        <Button
          variant="outline"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="w-full justify-between mb-4"
        >
          <span className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filtres
            {Object.keys(activeFilters).length > 0 && (
              <span className="bg-primary text-primary-foreground px-2 py-0.5 rounded-full text-xs">
                {Object.keys(activeFilters).length}
              </span>
            )}
          </span>
          {mobileOpen ? <X className="h-4 w-4" /> : null}
        </Button>

        {/* Mobile Drawer */}
        {mobileOpen && (
          <div className="bg-background border rounded-lg p-4 mb-4">
            <FilterContent />
          </div>
        )}
      </div>
    </>
  );
};

export default SidebarFilter;
